/**
 * In-memory customer satisfaction surveys for the mock API client (tracker
 * item 69). Same rules as the backend where the UI depends on them: staff
 * routes are ADMIN / OFFICE_MANAGER only (settings are written by ADMIN
 * only), a job's survey needs a completed job and the survey message turned
 * on, a send / resend rotates the link, a public link answers once and gives
 * the same "not found" for any unknown token, a low score raises a staff
 * alert. Not modelled: the delay and the worker (requests are sent at once by
 * the office's button; `scheduleForJob` makes a PENDING one for tests), the
 * cooldown and flat-contract rules, rate limits.
 */
import { AxiosError, AxiosHeaders, AxiosResponse } from 'axios';
import * as types from './types';

function apiError(status: number, message: string, code?: string): AxiosError {
  const config = { headers: new AxiosHeaders() };
  const response = { status, statusText: '', headers: {}, config, data: { success: false, message, ...(code ? { code } : {}) } } as AxiosResponse;
  return new AxiosError(message, undefined, config, undefined, response);
}

export interface MockSurveyUser {
  id: string;
  tenantId: string;
  role: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
}

export interface MockSurveyJob {
  id: string;
  status: string;
  customerId: string;
  assignedTo: string | null;
  scheduledDate: string | null;
  completedDate?: string | null;
}

export interface MockSurveyCustomer {
  id: string;
  name: string;
  email?: string | null;
  notificationOptOut?: boolean;
}

export interface MockSurveyHost {
  /** The signed-in user (throws when signed out) */
  currentUser(): MockSurveyUser;
  users(tenantId: string): MockSurveyUser[];
  job(tenantId: string, jobId: string): MockSurveyJob | null;
  customer(tenantId: string, customerId: string): MockSurveyCustomer | null;
  companyName(tenantId: string): string;
  /** The company's notification master switch and the survey template's switch */
  notifications(tenantId: string): { enabled: boolean; templateActive: boolean };
  /** Turn the survey template on (the mode going ON does it) */
  activateTemplate(tenantId: string): void;
  /** Raise the staff low-score alert */
  alertLowScore?(tenantId: string, input: { jobId: string; responseId: string; title: string; body: string }): void;
}

const STAFF = ['ADMIN', 'OFFICE_MANAGER'];
export const MOCK_SURVEY_LABELS: Record<number, string> = { 1: 'Poor', 2: 'Fair', 3: 'Good', 4: 'Very good', 5: 'Excellent' };
const SCALE: types.SurveyScale = { min: 1, max: 5, labels: MOCK_SURVEY_LABELS };
const DEFAULT_PROMPT = 'Anything you would like to tell us? (optional)';
const DAY = 24 * 60 * 60 * 1000;

interface Settings {
  mode: types.SurveyMode;
  delayHours: number;
  lowScoreThreshold: number;
  customerCooldownDays: number;
  commentPrompt: string | null;
  activeSince: string | null;
}

interface Request extends types.SurveyRequestSummary {
  tenantId: string;
  jobId: string;
  customerId: string;
  technicianUserId: string | null;
  token: string | null;
}

interface Response {
  id: string;
  tenantId: string;
  requestId: string;
  jobId: string;
  customerId: string;
  technicianUserId: string | null;
  rating: number;
  comment: string | null;
  createdAt: string;
}

let sequence = 0;
const nextId = (prefix: string) => `${prefix}-${++sequence}`;

function newToken(): string {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-';
  let token = '';
  for (let i = 0; i < 43; i++) token += alphabet[Math.floor(Math.random() * alphabet.length)];
  return token;
}

export class MockSurveys {
  private settings = new Map<string, Settings>();
  private requests: Request[] = [];
  private responses: Response[] = [];
  private lastKeys = new Map<string, string>();

  constructor(private readonly host: MockSurveyHost, private readonly clock: () => Date = () => new Date()) {}

  // ---------------------------------------------------------------- helpers

  private staff(adminOnly = false): MockSurveyUser {
    const actor = this.host.currentUser();
    if (!(adminOnly ? actor.role === 'ADMIN' : STAFF.includes(actor.role))) {
      throw apiError(403, 'Insufficient permissions', 'INSUFFICIENT_PERMISSIONS');
    }
    return actor;
  }

  private settingsOf(tenantId: string): Settings {
    return (
      this.settings.get(tenantId) ?? { mode: 'OFF', delayHours: 24, lowScoreThreshold: 2, customerCooldownDays: 30, commentPrompt: null, activeSince: null }
    );
  }

  private personName(tenantId: string, userId: string | null): { id: string; name: string } | null {
    if (!userId) return null;
    const user = this.host.users(tenantId).find((u) => u.id === userId);
    const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ');
    return { id: userId, name: user ? name || user.email : 'Former technician' };
  }

  private effective(request: Request): types.SurveyRequestStatus {
    return request.status === 'SENT' && request.expiresAt && new Date(request.expiresAt).getTime() <= this.clock().getTime() ? 'EXPIRED' : request.status;
  }

  private summary(request: Request): types.SurveyRequestSummary {
    return {
      id: request.id,
      status: this.effective(request),
      scheduledFor: request.scheduledFor,
      sentAt: request.sentAt,
      expiresAt: request.expiresAt,
      sendCount: request.sendCount,
      answeredAt: request.answeredAt,
      cancelReason: request.cancelReason,
      cancelReasonText: request.cancelReasonText,
    };
  }

  // ---------------------------------------------------------------- settings

  getSettings(): types.SurveySettings {
    const actor = this.staff();
    const notifications = this.host.notifications(actor.tenantId);
    return {
      ...this.settingsOf(actor.tenantId),
      notificationsEnabled: notifications.enabled,
      eventActive: notifications.templateActive,
      scale: SCALE,
    };
  }

  updateSettings(input: types.UpdateSurveySettingsInput): types.SurveySettings {
    const actor = this.staff(true);
    const current = this.settingsOf(actor.tenantId);
    const body = (input ?? {}) as Record<string, unknown>;
    const known = ['mode', 'delayHours', 'lowScoreThreshold', 'customerCooldownDays', 'commentPrompt'];
    if (!known.some((key) => body[key] !== undefined)) throw apiError(400, 'Nothing to change', 'VALIDATION_ERROR');
    const next = { ...current };
    const whole = (name: string, min: number, max: number): number => {
      const value = body[name];
      if (typeof value !== 'number' || !Number.isInteger(value) || value < min || value > max) {
        throw apiError(400, `${name}: must be a whole number from ${min} to ${max}`, 'VALIDATION_ERROR');
      }
      return value;
    };
    if (body.mode !== undefined) {
      if (body.mode !== 'OFF' && body.mode !== 'ON') throw apiError(400, 'mode: must be one of OFF, ON', 'VALIDATION_ERROR');
      next.mode = body.mode;
    }
    if (body.delayHours !== undefined) next.delayHours = whole('delayHours', 0, 720);
    if (body.lowScoreThreshold !== undefined) next.lowScoreThreshold = whole('lowScoreThreshold', 1, 4);
    if (body.customerCooldownDays !== undefined) next.customerCooldownDays = whole('customerCooldownDays', 0, 365);
    if (body.commentPrompt !== undefined) {
      const prompt = String(body.commentPrompt ?? '').replace(/\s+/g, ' ').trim();
      if (prompt.length > 300) throw apiError(400, 'commentPrompt: at most 300 characters', 'VALIDATION_ERROR');
      next.commentPrompt = prompt || null;
    }
    if (current.mode === 'OFF' && next.mode === 'ON') {
      next.activeSince = this.clock().toISOString().slice(0, 10);
      this.host.activateTemplate(actor.tenantId);
    }
    this.settings.set(actor.tenantId, next);
    return this.getSettings();
  }

  // ---------------------------------------------------------------- job page

  private requestOf(tenantId: string, jobId: string): Request | undefined {
    return this.requests.find((r) => r.tenantId === tenantId && r.jobId === jobId);
  }

  private jobOrThrow(tenantId: string, jobId: string): MockSurveyJob {
    const job = this.host.job(tenantId, jobId);
    if (!job) throw apiError(404, 'Job not found', 'NOT_FOUND');
    return job;
  }

  private offMessage(enabled: boolean): string {
    return enabled
      ? 'The survey message is turned off (Settings > Notifications)'
      : 'Notifications are turned off for this company (Settings > Notifications)';
  }

  getJobSurvey(jobId: string): types.JobSurvey {
    const actor = this.staff();
    const job = this.jobOrThrow(actor.tenantId, jobId);
    const settings = this.settingsOf(actor.tenantId);
    const notifications = this.host.notifications(actor.tenantId);
    const enabled = notifications.enabled && notifications.templateActive;
    const request = this.requestOf(actor.tenantId, jobId);
    const response = request ? this.responses.find((r) => r.requestId === request.id) : undefined;
    const customer = this.host.customer(actor.tenantId, job.customerId);
    const done = job.status === 'COMPLETED' || job.status === 'INVOICED';
    const answered = request ? this.effective(request) === 'ANSWERED' : false;
    const problem = !done
      ? `A survey can only be sent once the job is completed (this job is ${job.status})`
      : answered
        ? 'The customer already answered this survey'
        : enabled
          ? null
          : this.offMessage(notifications.enabled);
    const skip: [string, string] | null = !enabled ? null
      : !(customer?.email ?? '').trim() ? ['NO_EMAIL', 'The customer has no email address']
        : customer?.notificationOptOut ? ['OPTED_OUT', 'The customer opted out of notifications'] : null;
    let automatic: { eligible: boolean; reason: string | null };
    if (request) automatic = { eligible: false, reason: 'A survey was already requested for this job' };
    else if (settings.mode !== 'ON') automatic = { eligible: false, reason: 'Automatic surveys are turned off (Settings > Surveys)' };
    else if (!done) automatic = { eligible: false, reason: 'The job is not completed' };
    else if (skip) automatic = { eligible: false, reason: skip[1] };
    else automatic = { eligible: true, reason: null };
    return {
      jobId,
      enabled,
      canSend: problem === null,
      unavailableReason: problem,
      wouldSkip: skip?.[0] ?? null,
      wouldSkipText: skip?.[1] ?? null,
      automatic,
      request: request ? this.summary(request) : null,
      response: response
        ? { rating: response.rating, comment: response.comment, createdAt: response.createdAt, lowScore: response.rating <= settings.lowScoreThreshold }
        : null,
    };
  }

  /** Test helper: what the worker does some hours after a completion (a PENDING request). */
  scheduleForJob(tenantId: string, jobId: string): types.SurveyRequestSummary | null {
    const job = this.host.job(tenantId, jobId);
    if (!job || this.requestOf(tenantId, jobId)) return null;
    const settings = this.settingsOf(tenantId);
    const request: Request = {
      id: nextId('survey-request'),
      tenantId,
      jobId,
      customerId: job.customerId,
      technicianUserId: job.assignedTo,
      token: null,
      status: 'PENDING',
      scheduledFor: new Date(this.clock().getTime() + settings.delayHours * 3_600_000).toISOString(),
      sentAt: null,
      expiresAt: null,
      sendCount: 0,
      answeredAt: null,
      cancelReason: null,
      cancelReasonText: null,
    };
    this.requests.push(request);
    return this.summary(request);
  }

  sendJobSurvey(jobId: string, idempotencyKey?: string): types.SendSurveyResult {
    const actor = this.staff();
    const job = this.jobOrThrow(actor.tenantId, jobId);
    if (job.status !== 'COMPLETED' && job.status !== 'INVOICED') {
      throw apiError(409, `A survey can only be sent once the job is completed (this job is ${job.status})`, 'JOB_NOT_COMPLETED');
    }
    const notifications = this.host.notifications(actor.tenantId);
    if (!notifications.enabled || !notifications.templateActive) {
      throw apiError(409, this.offMessage(notifications.enabled), 'NOTIFICATION_OFF');
    }
    let request = this.requestOf(actor.tenantId, jobId);
    if (request && this.effective(request) === 'ANSWERED') {
      throw apiError(409, 'The customer already answered this survey', 'SURVEY_ALREADY_ANSWERED');
    }
    const key = idempotencyKey ?? `minute-${Math.floor(this.clock().getTime() / 60_000)}`;
    if (request && this.lastKeys.get(request.id) === key) {
      return { request: this.summary(request), message: { id: `${request.id}-msg-${request.sendCount}`, status: 'PENDING', skipReason: null, skipReasonText: null }, deduplicated: true };
    }
    const now = this.clock();
    if (!request) {
      request = {
        id: nextId('survey-request'), tenantId: actor.tenantId, jobId, customerId: job.customerId, technicianUserId: job.assignedTo,
        token: null, status: 'PENDING', scheduledFor: now.toISOString(), sentAt: null, expiresAt: null, sendCount: 0, answeredAt: null,
        cancelReason: null, cancelReasonText: null,
      };
      this.requests.push(request);
    }
    const customer = this.host.customer(actor.tenantId, job.customerId);
    const skip: [string, string] | null = !(customer?.email ?? '').trim() ? ['NO_EMAIL', 'The customer has no email address']
      : customer?.notificationOptOut ? ['OPTED_OUT', 'The customer opted out of notifications'] : null;
    this.lastKeys.set(request.id, key);
    if (skip) {
      if (request.status !== 'SENT') {
        request.status = 'CANCELLED';
        request.cancelReason = skip[0];
        request.cancelReasonText = skip[1];
        request.token = null;
      }
      return { request: this.summary(request), message: { id: `${request.id}-msg`, status: 'SKIPPED', skipReason: skip[0], skipReasonText: skip[1] }, deduplicated: false };
    }
    request.status = 'SENT';
    request.token = newToken();
    request.sentAt = now.toISOString();
    request.expiresAt = new Date(now.getTime() + 30 * DAY).toISOString();
    request.sendCount += 1;
    request.cancelReason = null;
    request.cancelReasonText = null;
    return { request: this.summary(request), message: { id: `${request.id}-msg-${request.sendCount}`, status: 'PENDING', skipReason: null, skipReasonText: null }, deduplicated: false };
  }

  /** Test helper: the live link secret of a job's survey (the real one exists only in the email). */
  tokenFor(tenantId: string, jobId: string): string | null {
    return this.requestOf(tenantId, jobId)?.token ?? null;
  }

  // ---------------------------------------------------------------- inbox

  list(query: types.SurveyListQuery = {}): types.SurveyListResponse {
    const actor = this.staff();
    const settings = this.settingsOf(actor.tenantId);
    let maxRating = query.maxRating ?? null;
    if (query.lowOnly) maxRating = Math.min(maxRating ?? settings.lowScoreThreshold, settings.lowScoreThreshold);
    const from = query.startDate ? new Date(`${query.startDate}T00:00:00.000Z`).getTime() : null;
    const to = query.endDate ? new Date(`${query.endDate}T00:00:00.000Z`).getTime() + DAY : null;
    for (const [name, value] of [['rating', query.rating], ['maxRating', query.maxRating]] as const) {
      if (value !== undefined && (!Number.isInteger(value) || value < 1 || value > 5)) throw apiError(400, `${name}: choose 1 to 5`, 'INVALID_FILTER');
    }
    if (query.startDate && query.endDate && query.startDate > query.endDate) throw apiError(400, 'startDate must not be after endDate', 'INVALID_FILTER');
    const rows = this.responses
      .filter((r) => r.tenantId === actor.tenantId)
      .filter((r) => !query.technicianId || r.technicianUserId === query.technicianId)
      .filter((r) => !query.jobId || r.jobId === query.jobId)
      .filter((r) => !query.customerId || r.customerId === query.customerId)
      .filter((r) => query.rating === undefined || r.rating === query.rating)
      .filter((r) => maxRating === null || r.rating <= maxRating)
      .filter((r) => from === null || new Date(r.createdAt).getTime() >= from)
      .filter((r) => to === null || new Date(r.createdAt).getTime() < to)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt) || b.id.localeCompare(a.id));
    const limit = Math.min(100, Math.max(1, query.limit ?? 20));
    const page = Math.max(1, query.page ?? 1);
    const slice = rows.slice((page - 1) * limit, page * limit);
    return {
      data: slice.map((r): types.SurveyResponseItem => {
        const job = this.host.job(actor.tenantId, r.jobId);
        return {
          id: r.id,
          requestId: r.requestId,
          rating: r.rating,
          comment: r.comment,
          createdAt: r.createdAt,
          lowScore: r.rating <= settings.lowScoreThreshold,
          job: { id: r.jobId, scheduledDate: job?.scheduledDate ?? null, completedDate: job?.completedDate ?? null },
          customer: { id: r.customerId, name: this.host.customer(actor.tenantId, r.customerId)?.name ?? 'Customer' },
          technician: this.personName(actor.tenantId, r.technicianUserId),
        };
      }),
      pagination: { page, limit, total: rows.length, pages: Math.max(1, Math.ceil(rows.length / limit)) },
      threshold: settings.lowScoreThreshold,
    };
  }

  // ---------------------------------------------------------------- report

  report(query: types.SatisfactionReportQuery = {}): types.SatisfactionReport {
    const actor = this.staff();
    const settings = this.settingsOf(actor.tenantId);
    const groupBy = query.groupBy ?? 'week';
    if (groupBy !== 'week' && groupBy !== 'month') throw apiError(400, 'groupBy: must be one of week, month', 'INVALID_FILTER');
    const endDate = query.endDate ?? this.clock().toISOString().slice(0, 10);
    const startDate = query.startDate ?? new Date(new Date(`${endDate}T00:00:00.000Z`).getTime() - 89 * DAY).toISOString().slice(0, 10);
    if (startDate > endDate) throw apiError(400, 'startDate must not be after endDate', 'INVALID_FILTER');
    const from = new Date(`${startDate}T00:00:00.000Z`).getTime();
    const to = new Date(`${endDate}T00:00:00.000Z`).getTime() + DAY;
    if ((to - from) / DAY > 366) throw apiError(400, 'The range can be at most 366 days', 'INVALID_FILTER');
    const inRange = (iso: string | null) => !!iso && new Date(iso).getTime() >= from && new Date(iso).getTime() < to;
    const mine = this.responses.filter((r) => r.tenantId === actor.tenantId && inRange(r.createdAt) && (!query.technicianId || r.technicianUserId === query.technicianId));
    const sent = this.requests.filter((r) => r.tenantId === actor.tenantId && inRange(r.sentAt) && (!query.technicianId || r.technicianUserId === query.technicianId));
    const answered = sent.filter((r) => r.status === 'ANSWERED').length;
    const round2 = (n: number) => Math.round(n * 100) / 100;
    const avg = (rows: Response[]) => rows.reduce((sum, r) => sum + r.rating, 0) / rows.length;
    const bucket = (iso: string): string => {
      const d = new Date(iso);
      if (groupBy === 'month') return `${d.toISOString().slice(0, 7)}-01`;
      const day = (d.getUTCDay() + 6) % 7;
      return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - day)).toISOString().slice(0, 10);
    };
    const trendMap = new Map<string, Response[]>();
    for (const r of mine) trendMap.set(bucket(r.createdAt), [...(trendMap.get(bucket(r.createdAt)) ?? []), r]);
    const techMap = new Map<string | null, Response[]>();
    for (const r of mine) techMap.set(r.technicianUserId, [...(techMap.get(r.technicianUserId) ?? []), r]);
    return {
      startDate,
      endDate,
      groupBy,
      threshold: settings.lowScoreThreshold,
      summary: {
        requestsSent: sent.length,
        requestsAnswered: answered,
        responseRate: sent.length > 0 ? Math.round((answered / sent.length) * 1000) / 1000 : null,
        responses: mine.length,
        averageRating: mine.length > 0 ? round2(avg(mine)) : null,
        lowScoreCount: mine.filter((r) => r.rating <= settings.lowScoreThreshold).length,
      },
      distribution: [1, 2, 3, 4, 5].map((rating) => {
        const count = mine.filter((r) => r.rating === rating).length;
        return { rating, label: MOCK_SURVEY_LABELS[rating], count, percent: mine.length > 0 ? Math.round((count / mine.length) * 1000) / 10 : 0 };
      }),
      trend: [...trendMap.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([periodStart, rows]) => ({
          periodStart,
          label: groupBy === 'month' ? periodStart.slice(0, 7) : `Week of ${periodStart}`,
          responses: rows.length,
          averageRating: round2(avg(rows)),
        })),
      byTechnician: [...techMap.entries()]
        .map(([technicianId, rows]) => ({
          technicianId,
          name: technicianId ? this.personName(actor.tenantId, technicianId)?.name ?? 'Former technician' : 'Unassigned',
          responses: rows.length,
          averageRating: round2(avg(rows)),
          lowScoreCount: rows.filter((r) => r.rating <= settings.lowScoreThreshold).length,
        }))
        .sort((a, b) => b.responses - a.responses || a.name.localeCompare(b.name)),
    };
  }

  // ---------------------------------------------------------------- public link

  private byToken(token: string): Request {
    if (!/^[A-Za-z0-9_-]{43}$/.test(token)) throw apiError(404, 'Not found');
    const request = this.requests.find((r) => r.token === token && (r.status === 'SENT' || r.status === 'ANSWERED' || r.status === 'EXPIRED'));
    if (!request) throw apiError(404, 'Not found');
    return request;
  }

  getPublic(token: string): types.PublicSurvey {
    const request = this.byToken(token);
    const job = this.host.job(request.tenantId, request.jobId);
    const customer = this.host.customer(request.tenantId, request.customerId);
    if (!job) throw apiError(404, 'Not found');
    const effective = this.effective(request);
    const response = this.responses.find((r) => r.requestId === request.id);
    const technician = this.host.users(request.tenantId).find((u) => u.id === (request.technicianUserId ?? job.assignedTo));
    return {
      status: effective === 'ANSWERED' ? 'ANSWERED' : effective === 'EXPIRED' ? 'EXPIRED' : 'OPEN',
      companyName: this.host.companyName(request.tenantId),
      customerFirstName: (customer?.name ?? '').trim().split(/\s+/)[0] || 'there',
      jobDate: job.completedDate ?? job.scheduledDate,
      serviceList: 'Your scheduled service',
      technicianFirstName: technician?.firstName?.trim() || null,
      commentPrompt: this.settingsOf(request.tenantId).commentPrompt ?? DEFAULT_PROMPT,
      scale: SCALE,
      commentMaxLength: 2000,
      rating: response?.rating ?? null,
    };
  }

  submitPublic(token: string, answer: types.PublicSurveyAnswer): { status: 'ANSWERED' } {
    const request = this.byToken(token);
    const rating = (answer as { rating?: unknown })?.rating;
    if (typeof rating !== 'number' || !Number.isInteger(rating) || rating < 1 || rating > 5) {
      throw apiError(400, 'rating: choose a whole number from 1 to 5', 'VALIDATION_ERROR');
    }
    const comment = typeof answer.comment === 'string' ? answer.comment.trim() : null;
    if (comment && comment.length > 2000) throw apiError(400, 'comment: at most 2000 characters', 'VALIDATION_ERROR');
    const effective = this.effective(request);
    if (effective === 'ANSWERED') throw apiError(409, 'This survey was already answered', 'SURVEY_ALREADY_ANSWERED');
    if (effective === 'EXPIRED') throw apiError(409, 'This survey link has expired', 'SURVEY_EXPIRED');
    const now = this.clock().toISOString();
    request.status = 'ANSWERED';
    request.answeredAt = now;
    const response: Response = {
      id: nextId('survey-response'),
      tenantId: request.tenantId,
      requestId: request.id,
      jobId: request.jobId,
      customerId: request.customerId,
      technicianUserId: request.technicianUserId,
      rating,
      comment: comment || null,
      createdAt: now,
    };
    this.responses.push(response);
    if (rating <= this.settingsOf(request.tenantId).lowScoreThreshold) {
      const customer = this.host.customer(request.tenantId, request.customerId);
      this.host.alertLowScore?.(request.tenantId, {
        jobId: request.jobId,
        responseId: response.id,
        title: `Low survey score (${rating} of 5) from ${customer?.name ?? 'a customer'}`,
        body: MOCK_SURVEY_LABELS[rating],
      });
    }
    return { status: 'ANSWERED' };
  }
}
